import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { shadowLight } from 'app/colors';
import { borderRadii } from 'app/spacing';
import useField from 'hooks/useField';
import DraggableItem from 'components/Kizen/DraggableItem';
import EditableText from 'components/Kizen/EditableText';
import Validation, { useValidation } from 'components/Inputs/Validation';
import { useTranslation } from 'react-i18next';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { isNonComposingEnterKeyboardEvent } from 'utility/keyboard';

export const fieldMargin = 7.5;

const FieldWrapper = styled(DraggableItem)`
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 10px;
  margin: ${fieldMargin}px;
  ${({ dragging }) => dragging && shadowLight}
  .FieldHandle {
    cursor: grab;
    margin-right: 10px;
  }
  && .FieldHandle:hover svg {
    color: var(--color-background-darker);
  }
  &:hover .FieldHandle svg {
    color: var(--color-background-dark);
  }
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: var(--color-background-light);
      && .FieldHandle,
      && .FieldHandle:hover {
        cursor: default;
        svg {
          color: var(--color-background-medium);
        }
      }
    `}
`;

const FieldLayout = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;

  // Input
  & > .editableText {
    min-width: 0;
    max-width: 500px;
    input[disabled] {
      background: none;
    }
  }
`;

export default function BuilderField(props) {
  const {
    field,
    onChangeDescription,
    onFocus: onFocusProp,
    onValidate,
    children,
    FieldBeforeText,
    Actions,
    ...others
  } = props;
  const { t } = useTranslation();

  const [stagedDescription, setStagedDescription] = useField(
    field.description,
    [field]
  );
  const fieldRef = useRef();
  const [validation, validationProps] = useValidation(fieldRef, {
    value: stagedDescription,
    validate: {
      full: (desc) => {
        if (!desc.trim()) {
          return t('This field is required');
        }
        if (onValidate) {
          return onValidate(desc, field);
        }
        return true;
      },
    },
  });

  // if it's a new field let's focus on it
  useEffect(() => {
    if (field?.newest && fieldRef.current) {
      fieldRef.current.focus();
    }
  }, [field]);

  const [tooltipProps, tooltip] = useTruncationTooltip();
  const [editing, setEditing] = useState(false);

  const onFocus = (...args) => {
    onFocusProp?.(...args);
    setEditing(true);
  };
  const onBlur = (...args) => {
    setEditing(false);
    if (onChangeDescription) {
      onChangeDescription(stagedDescription, field);
    }
    validation.onBlur(...args);
  };

  return (
    <FieldWrapper
      disabled={field.isHidden}
      data-qa-field-id={field.id}
      {...others}
    >
      <FieldLayout>
        {FieldBeforeText && <FieldBeforeText field={field} {...others} />}
        {!editing && tooltip}
        <EditableText
          ref={fieldRef}
          disabled={field.isDefault}
          value={stagedDescription}
          onChange={setStagedDescription}
          {...validation}
          {...tooltipProps}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={(ev) => {
            if (isNonComposingEnterKeyboardEvent(ev)) {
              fieldRef.current.blur();
            }
          }}
          className="editableText"
        />
        <Validation inModal {...validationProps} />
      </FieldLayout>
      {children}
      {Actions && <Actions field={field} {...others} />}
    </FieldWrapper>
  );
}

export const FieldPlaceholder = styled.div`
  position: absolute;
  top: ${fieldMargin}px;
  bottom: ${fieldMargin}px;
  left: ${fieldMargin}px;
  right: ${fieldMargin}px;
  background-color: var(--color-background-light);
  border: 1px dashed var(--color-border-medium);
  border-radius: ${borderRadii.standard};
`;
